import { useCallback, useMemo, useState } from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  useGetMatchRun,
  useGetMatchRunDetails,
  useListMatchRuns,
} from "src/generated/hooks/form-service/queries";
import type {
  GetMatchRunResponse,
  ListMatchRunsResponse,
  MatchRunListItem,
} from "src/generated/hooks/form-service/requests";
import { createMatchRun } from "src/generated/hooks/form-service/requests";
import { exponentialBackoff } from "src/hooks/useDynamicPolling";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import type { BulkTargets } from "src/scenes/orgAdmin/forms/types";
import { GET_MATCH_DETAILS } from "src/scenes/orgAdmin/match/graphql/queries";
import { throwIfFormServiceError } from "src/services/error";
import * as GQL from "src/types/graphql";
import * as RemoteData from "src/types/remoteData";
import { z } from "zod";

const POLL_BASE_INTERVAL_MS = 1_000;
const POLL_MAX_INTERVAL_MS = 5_000;
const MAX_NOT_FOUND_RETRIES = 10;

const getBackoffPollInterval = exponentialBackoff(
  POLL_BASE_INTERVAL_MS,
  1.5,
  POLL_MAX_INTERVAL_MS
);

const StartMatchResponseSchema = z.object({
  matchRunId: z.string().uuid(),
});

export type StartFormServiceMatchInput = {
  matchName?: string;
  tagLabel?: string;
  enrollmentPeriodId: uuid;
  targets: BulkTargets;
};

export type FormServiceMatchRun = GetMatchRunResponse;
export type FormServiceMatchRunListItem = MatchRunListItem;
export type FormServiceMatchRunsList = ListMatchRunsResponse;

export type MatchDetailsQueryData = {
  match_run_by_pk: {
    name: string | null;
    created_at: string;
    status: string | null;
    status_message: string | null;
    results_document_id: string | null;
    match_config_snapshot_document_id: string | null;
    capacities_snapshot_document_id: string | null;
  } | null;
};

export type ListFormServiceMatchRunsInput = {
  enrollmentPeriodId: string;
  limit: number;
  offset: number;
  sortKey?: "created_at";
  sortType?: "asc" | "desc";
  schoolIds?: string[];
  enabled?: boolean;
};

export function useFormServiceMatch() {
  const [matchRunError, setMatchRunError] = useState<string | null>(null);

  const startMatch = useCallback(
    async ({
      matchName,
      tagLabel,
      enrollmentPeriodId,
      targets,
    }: StartFormServiceMatchInput) => {
      try {
        const response = throwIfFormServiceError(
          await createMatchRun({
            body: {
              ...targets,
              matchName,
              tagLabel,
              enrollmentPeriodId,
            },
          })
        );
        return StartMatchResponseSchema.parse(response.data);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Match service error";
        setMatchRunError(message);
        throw error;
      }
    },
    []
  );

  return {
    startMatch,
    matchRunError,
  };
}

function isTerminalMatchStatus(status: FormServiceMatchRun["status"]) {
  return status === "Completed" || status === "Canceled" || status === "Error";
}

function getPollIntervalMs(pollAttempt: number): number {
  let interval = POLL_BASE_INTERVAL_MS;
  for (let attempt = 0; attempt < pollAttempt; attempt++) {
    interval = getBackoffPollInterval(interval);
  }
  return interval;
}

function isMatchRunNotFound(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const withStatus = error as {
    status?: number;
    response?: { status?: number };
  };
  if (withStatus.status === 404 || withStatus.response?.status === 404) {
    return true;
  }
  const message =
    "message" in error && typeof error.message === "string"
      ? error.message
      : "error" in error &&
        typeof error.error === "object" &&
        error.error &&
        "message" in error.error &&
        typeof error.error.message === "string"
      ? error.error.message
      : "";
  return /not found/i.test(message);
}

export function useFormServiceMatchResults(matchId?: string) {
  const clientOptions = useMemo(
    () => ({
      path: { matchRunId: matchId ?? "" },
      throwOnError: true as const,
    }),
    [matchId]
  );

  const matchRunQuery = useGetMatchRun(clientOptions, undefined, {
    enabled: Boolean(matchId),
    refetchOnMount: "always",
    retry: (failureCount, error) =>
      isMatchRunNotFound(error) && failureCount < MAX_NOT_FOUND_RETRIES,
    retryDelay: (attemptIndex) => getPollIntervalMs(attemptIndex),
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const matchRun = query.state.data?.data;
      if (!matchRun || isTerminalMatchStatus(matchRun.status)) {
        return false;
      }
      const pollAttempt = Math.max(0, query.state.dataUpdateCount - 1);
      return getPollIntervalMs(pollAttempt);
    },
  });

  const matchRun =
    matchId && matchRunQuery.data?.data?.id === matchId
      ? matchRunQuery.data.data
      : null;

  const matchRunError = (() => {
    if (matchRun?.status === "Error") {
      return `Match service error: ${matchRun.statusMessage ?? ""}`;
    }
    if (!matchRunQuery.isError) return null;
    if (isMatchRunNotFound(matchRunQuery.error)) {
      return "Match run not found";
    }
    const error = matchRunQuery.error;
    if (error instanceof Error) return error.message;
    if (
      error &&
      typeof error === "object" &&
      "error" in error &&
      typeof error.error === "object" &&
      error.error &&
      "message" in error.error &&
      typeof error.error.message === "string"
    ) {
      return error.error.message;
    }
    return "Match service error";
  })();

  const matchProgress = useMemo(() => {
    if (!matchRun || matchRun.totalFormCount === 0) return null;
    return (
      (matchRun.loadedFormCount + matchRun.matchedFormCount) /
      (2 * matchRun.totalFormCount)
    );
  }, [matchRun]);

  return {
    matchRun,
    matchProgress,
    matchRunError,
  };
}

export function useFormServiceMatchRunsList(
  input: ListFormServiceMatchRunsInput
) {
  const hasEmptySchoolFilter =
    input.schoolIds !== undefined && input.schoolIds.length === 0;
  const schoolIdsQuery = input.schoolIds?.length
    ? input.schoolIds.join(",")
    : undefined;

  const clientOptions = useMemo(
    () => ({
      query: {
        enrollmentPeriodId: input.enrollmentPeriodId,
        limit: input.limit,
        offset: input.offset,
        sortKey: input.sortKey ?? "created_at",
        sortType: input.sortType ?? "desc",
        ...(schoolIdsQuery ? { schoolIds: schoolIdsQuery } : {}),
      },
      throwOnError: true as const,
    }),
    [
      input.enrollmentPeriodId,
      input.limit,
      input.offset,
      input.sortKey,
      input.sortType,
      schoolIdsQuery,
    ]
  );

  const queryEnabled =
    (input.enabled ?? Boolean(input.enrollmentPeriodId)) &&
    !hasEmptySchoolFilter;

  const query = useListMatchRuns(clientOptions, undefined, {
    enabled: queryEnabled,
    refetchOnMount: "always",
  });

  const remoteData = useMemo(() => {
    if (hasEmptySchoolFilter) {
      return RemoteData.success<unknown, FormServiceMatchRunsList>({
        matchRuns: [],
        totalCount: 0,
      });
    }
    return RemoteData.fromRestResult<unknown, FormServiceMatchRunsList>({
      data: query.data?.data,
      error: query.error,
      isLoading: query.isLoading,
      isRefetching: query.isRefetching,
      isError: query.isError,
    });
  }, [
    hasEmptySchoolFilter,
    query.data?.data,
    query.error,
    query.isLoading,
    query.isRefetching,
    query.isError,
  ]);

  return { remoteData, refetch: query.refetch };
}

export function useMatchDetails(matchId?: string) {
  const flags = useFlags(["form-service-match"]);
  const useFormService = flags["form-service-match"].enabled;

  const { remoteData: gqlRemoteData } = useRemoteDataQuery<
    GQL.GetMatchDetails,
    GQL.GetMatchDetailsVariables
  >(GET_MATCH_DETAILS, {
    variables: { matchId: matchId ?? "" },
    skip: !matchId || useFormService,
    fetchPolicy: "cache-and-network",
  });

  const restClientOptions = useMemo(
    () => ({
      path: { matchRunId: matchId ?? "" },
      throwOnError: true as const,
    }),
    [matchId]
  );

  const restQuery = useGetMatchRunDetails(restClientOptions, undefined, {
    enabled: Boolean(matchId) && useFormService,
    refetchOnMount: "always",
  });

  const restData = restQuery.data?.data;
  const mappedRestData = useMemo<MatchDetailsQueryData | undefined>(() => {
    if (restData == null) return undefined;
    return {
      match_run_by_pk: {
        name: restData.name,
        created_at: restData.createdAt,
        status: restData.status,
        status_message: restData.statusMessage,
        results_document_id: restData.resultsDocumentId,
        match_config_snapshot_document_id:
          restData.matchConfigSnapshotDocumentId,
        capacities_snapshot_document_id: restData.capacitiesSnapshotDocumentId,
      },
    };
  }, [restData]);

  const restRemoteData = useMemo(
    () =>
      RemoteData.fromRestResult<Error, MatchDetailsQueryData>({
        data: mappedRestData,
        error: RemoteData.errorResponseToError(
          restQuery.error as { error: { message: string } } | Error | null
        ),
        isLoading: restQuery.isLoading,
        isRefetching: restQuery.isRefetching,
        isError: restQuery.isError,
      }),
    [
      mappedRestData,
      restQuery.error,
      restQuery.isLoading,
      restQuery.isRefetching,
      restQuery.isError,
    ]
  );

  if (useFormService) {
    return restRemoteData;
  }
  return gqlRemoteData as RemoteData.RemoteData<Error, MatchDetailsQueryData>;
}
